/**
 * President bot.
 *
 * - Leading: plays its lowest rank, as the largest set it holds of that rank, so low cards go first and big
 *   cards are kept to take control later. Twos are kept until the end unless they finish the game.
 * - Following: plays the lowest set that beats the pile, but passes rather than spend a two or an ace on a low
 *   pile while it still holds many cards; it always plays if that empties its hand.
 * The bot only ever reads its own seat's view.
 */
import { type CardId } from '../../core/cards.js';
import { chance, type BotContext, type BotEngine } from '../../bots/types.js';
import { presidentLegal, presidentPower, type PresidentAction, type PresidentLegal, type PresidentView } from './engine.js';

const power = (set: readonly CardId[]) => presidentPower(set[0]!);

function choose(view: PresidentView, legal: PresidentLegal, ctx: BotContext): PresidentAction {
  const sets = legal.sets;
  const finisher = sets.find((s) => s.length === view.hand.length);
  if (finisher) return { type: 'play', cards: finisher };
  const cheapest = [...sets].sort((a, b) => power(a) - power(b) || b.length - a.length)[0]!;

  if (view.need === 0) {
    // Lead the lowest rank, with every card of that rank so it is gone in one go.
    const lowestPower = power(cheapest);
    const all = sets.filter((s) => power(s) === lowestPower).sort((a, b) => b.length - a.length)[0]!;
    return { type: 'play', cards: all };
  }
  if (sets.length === 0) return { type: 'pass' };
  const careful = ctx.profile.memory > 0 && view.hand.length > 4;
  if (careful && power(cheapest) >= 14) return { type: 'pass' };
  return { type: 'play', cards: cheapest };
}

export const presidentBot: BotEngine<PresidentView, PresidentLegal, PresidentAction> = {
  decideAction(view, legal, ctx) {
    if (legal.sets.length === 0) return { type: 'pass' };
    if (chance(ctx, ctx.profile.mistakeRate)) {
      if (legal.canPass && chance(ctx, 0.5)) return { type: 'pass' };
      return { type: 'play', cards: legal.sets[ctx.rng.int(legal.sets.length)]! };
    }
    return choose(view, legal, ctx);
  },

  evaluateState(view) {
    if (view.seat === null) return 0;
    const mine = view.handCounts[view.seat]!;
    const others = view.handCounts.filter((_, s) => s !== view.seat);
    const avg = others.reduce((a, b) => a + b, 0) / others.length;
    return Math.max(0, Math.min(1, 0.5 + (avg - mine) / 26));
  },

  calculateRisk(view) {
    return view.seat === null ? 0 : Math.min(1, view.handCounts[view.seat]! / 13);
  },

  handleTimeout(view, legal) {
    const now = legal.sets.length > 0 || legal.canPass ? legal : presidentLegal(view.hand, view.pile);
    if (now.canPass) return { type: 'pass' };
    return { type: 'play', cards: [...now.sets].sort((a, b) => power(a) - power(b))[0]! };
  },
};
